/**
 * VersionListItem — single row in the pricing component versions list.
 *
 * Extracted from `BillingPricing.tsx#PricingDetailModal` (was 318 lines).
 * Owns rendering of: badges (AKTYWNA/SEED), calculator info, validity range,
 * calculator config (CalculatorDisplay), and per-version action buttons
 * (Skróć ważność / Wymień wersję / Usuń).
 *
 * Disabling rules:
 *   - `canClose === false` → "Skróć ważność" disabled
 *   - `canDelete === false` → "Usuń" disabled (with `cannotDeleteReasons` tooltip)
 *   - `canReplace === false` → "Wymień wersję" hidden
 *   - `disabled === true` (any modal opening) → all three buttons disabled
 *     (UX MEDIUM batch E task 4: race-click prevention)
 *
 * @see AC-9..AC-12 in docs/specifications/2026-05-05-billing-pricing-admin-ui/spec.md
 */
import React from "react";
import type { PricingComponentVersion } from "../../../../types/adminBilling";
import { CalculatorDisplay } from "./CalculatorDisplay";

export interface VersionListItemProps {
  version: PricingComponentVersion;
  /** Whether the version is currently active (validFrom ≤ now < validTo). */
  isActive: boolean;
  /** Whether the version is managed by seed runner (DELETE/replace blocked). */
  seedManaged: boolean;
  /** Backend-resolved capability — if false, "Skróć ważność" is disabled. */
  canClose: boolean;
  /** Backend-resolved capability — if false, "Usuń" is disabled. */
  canDelete: boolean;
  /** UI gating — if false, "Wymień wersję" button is hidden. */
  canReplace: boolean;
  /** Backend-supplied human-readable reasons; rendered into delete tooltip. */
  cannotDeleteReasons: string[];
  /** Master disable for race-click prevention while a modal is loading. */
  disabled?: boolean;
  onClose: (v: PricingComponentVersion) => void;
  onReplace: (v: PricingComponentVersion) => void;
  onDelete: (v: PricingComponentVersion) => void;
}

const formatDate = (date?: string | null): string => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("pl-PL");
};

export const VersionListItem: React.FC<VersionListItemProps> = ({
  version,
  isActive,
  seedManaged,
  canClose,
  canDelete,
  canReplace,
  cannotDeleteReasons,
  disabled = false,
  onClose,
  onReplace,
  onDelete,
}) => {
  const deleteTooltip =
    cannotDeleteReasons.length > 0
      ? `Nie można usunąć: ${cannotDeleteReasons.join(", ")}`
      : "Usuń wersję (DELETE)";
  const versionShortId = version.id.slice(0, 8);

  return (
    <div
      className={`border rounded-md p-4 ${
        isActive
          ? "bg-green-50 border-green-200"
          : "bg-neutral-50 border-neutral-200"
      }`}
    >
      <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {isActive && (
            <span className="text-xs bg-green-200 text-green-800 font-medium px-2 py-0.5 rounded">
              AKTYWNA
            </span>
          )}
          {seedManaged && (
            <span
              className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded"
              title="Nie można usunąć ani wymienić — zarządzane przez seed"
            >
              SEED
            </span>
          )}
          <span className="text-xs font-medium text-neutral-700">
            Kalkulator:{" "}
          </span>
          {version.calculator ? (
            <>
              <code className="text-xs bg-white border border-neutral-200 px-1.5 py-0.5 rounded">
                {version.calculator.name}
              </code>
              <span className="ml-1 text-xs text-neutral-400">
                ({version.calculator.type})
              </span>
            </>
          ) : (
            <span className="text-xs text-neutral-400 italic">
              brak kalkulatora
            </span>
          )}
        </div>
        <span className="text-xs text-neutral-500">
          {formatDate(version.validFrom)} — {formatDate(version.validTo)}
        </span>
      </div>
      {version.calculator && (
        <CalculatorDisplay calculator={version.calculator} mode="full" />
      )}
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-neutral-200">
        <button
          type="button"
          onClick={() => onClose(version)}
          disabled={!canClose || disabled}
          aria-label={`Skróć ważność wersji ${versionShortId}`}
          className="text-xs px-2 py-1 bg-neutral-100 text-neutral-700 rounded hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
          title="PATCH validTo"
        >
          Skróć ważność
        </button>
        {canReplace && (
          <button
            type="button"
            onClick={() => onReplace(version)}
            disabled={disabled}
            aria-label={`Wymień wersję ${versionShortId}`}
            className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Atomic replacement: PATCH validTo poprzedniej + INSERT nowej"
          >
            Wymień wersję
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(version)}
          disabled={!canDelete || disabled}
          aria-label={`Usuń wersję ${versionShortId}`}
          title={deleteTooltip}
          className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Usuń
        </button>
      </div>
    </div>
  );
};
